import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "reactstrap";
import { useTranslation } from "react-i18next";

import Step1WelcomeOffer from "./onboarding/Step1WelcomeOffer";
import Step2LicenseChoice from "./onboarding/Step2LicenseChoice";
import Step3Terms from "./onboarding/Step3Terms";
import Step4DesignService from "./onboarding/Step4DesignService";
import Step5SubscribeGate from "./onboarding/Step5SubscribeGate";
import {
  getWizardState,
  markDismissed,
  saveWizardState,
} from "./onboarding/onboardingStorage";

import "./onboarding/style.scss";

const STEPS = [
  Step1WelcomeOffer,
  Step2LicenseChoice,
  Step3Terms,
  Step4DesignService,
  Step5SubscribeGate,
];

const TOTAL_STEPS = STEPS.length;

export const ModalOnboardingWizard = ({
  isOpen,
  storeKey,
  onClose,
  onSubscribe,
}) => {
  const { t } = useTranslation();

  const initialState = useMemo(() => getWizardState(storeKey), [storeKey]);
  const [step, setStep] = useState(initialState.step || 1);
  const [answers, setAnswers] = useState(initialState.answers);

  // Si cambia el storeKey (cambio de tienda en runtime), resetea desde storage.
  useEffect(() => {
    const fresh = getWizardState(storeKey);
    setStep(fresh.step || 1);
    setAnswers(fresh.answers);
  }, [storeKey]);

  const persist = (nextStep, nextAnswers) => {
    saveWizardState(storeKey, {
      step: nextStep,
      answers: nextAnswers,
    });
  };

  const updateAnswers = (partial) => {
    setAnswers((prev) => {
      const next = { ...prev, ...partial };
      persist(step, next);
      return next;
    });
  };

  const StepComponent = STEPS[step - 1];
  const canAdvance =
    typeof StepComponent.canAdvance === "function"
      ? StepComponent.canAdvance(answers)
      : true;
  const hideFooter = StepComponent.hideFooter === true;

  const goNext = () => {
    setStep((prev) => {
      if (prev >= TOTAL_STEPS) {
        return prev;
      }
      const nextStep = prev + 1;
      persist(nextStep, answers);
      return nextStep;
    });
  };

  const goBack = () => {
    if (step > 1) {
      const prevStep = step - 1;
      setStep(prevStep);
      persist(prevStep, answers);
    }
  };

  const handleSkip = () => {
    if (typeof onClose === "function") {
      markDismissed(storeKey);
      onClose();
    }
  };

  const handleSubscribeClick = () => {
    persist(step, answers);
    if (typeof onSubscribe === "function") {
      onSubscribe(answers);
    }
  };

  const isLastStep = step === TOTAL_STEPS;
  const isFirstStep = step === 1;

  return (
    <Modal
      isOpen={isOpen}
      backdrop="static"
      keyboard={false}
      className="modal-dialog-centered transitionSelector preview modalOnboardingWizard isOpen"
    >
      <section>
        <div className="modal__container">
          <div className="onboarding-header">
            <div className="onboarding-header__progress">
              <div
                className="onboarding-header__progress-bar"
                style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
              />
            </div>
            <div className="onboarding-header__step-label">
              {t("homeModalWelcome.nav.stepLabel", {
                current: step,
                total: TOTAL_STEPS,
              })}
            </div>
          </div>

          <div className="onboarding-body">
            <StepComponent
              answers={answers}
              updateAnswers={updateAnswers}
              goNext={goNext}
              goBack={goBack}
              onSkip={handleSkip}
            />
          </div>

          {!hideFooter && (
            <div className="onboarding-footer">
              <div className="onboarding-footer__left">
                {!isFirstStep && (
                  <button
                    type="button"
                    className="close__button modal-button cancel-button"
                    onClick={goBack}
                  >
                    {t("homeModalWelcome.nav.back")}
                  </button>
                )}
              </div>
              <div className="onboarding-footer__right">
                {!isLastStep && (
                  <button
                    type="button"
                    className="close__button modal-button skip-button"
                    onClick={handleSkip}
                  >
                    {t("homeModalWelcome.nav.skip")}
                  </button>
                )}
                {isLastStep ? (
                  <button
                    type="button"
                    className="close__button modal-button save-button"
                    onClick={handleSubscribeClick}
                  >
                    {t("homeModalWelcome.nav.continueToPayment")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="close__button modal-button save-button"
                    onClick={goNext}
                    disabled={!canAdvance}
                  >
                    {t("homeModalWelcome.nav.next")}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </Modal>
  );
};

export default ModalOnboardingWizard;
